import LZString from "lz-string";

export class BrowserStorage<T> {
  private readonly storage: Storage | null;
  private readonly dataIdentifier: string;
  private readonly toBeCompressed: boolean;

  public static isStorageTypeSupported(type: "localStorage" | "sessionStorage"): boolean {
    try {
      // throws an error if storage type is not supported or if disabled by user
      // Also test some special characters in key and value
      const storage = window[type];
      const key = "t?e=s&t";
      storage.setItem(key, key);
      storage.getItem(key);
      storage.removeItem(key);

      return true;
    } catch (e) {
      return false;
    }
  }

  constructor(type: "localStorage" | "sessionStorage", dataIdentifier: string, toBeCompressed = false) {
    if (BrowserStorage.isStorageTypeSupported(type)) {
      this.storage = window[type];
      this.dataIdentifier = dataIdentifier;
      this.toBeCompressed = toBeCompressed;
    } else {
      this.storage = null;
      this.dataIdentifier = "null";
      this.toBeCompressed = false;
    }
  }

  private key(storageEntryKey: string | number): string {
    if (storageEntryKey.toString().startsWith(this.dataIdentifier)) {
      return storageEntryKey.toString();
    }

    return `${this.dataIdentifier}-${storageEntryKey.toString()}`;
  }

  clear = (): void => {
    if (this.storage) this.storage.clear();
  };

  write = (storageEntryKey: number | string, data: T): void => {
    if (this.storage) {
      let stringified = JSON.stringify(data);

      if (this.toBeCompressed) {
        stringified = LZString.compressToUTF16(stringified);
      }

      this.storage.setItem(this.key(storageEntryKey), stringified);
    }
  };

  read = (key: string): T | null => {
    if (!this.storage) {
      return null;
    }
    try {
      let storageContent = this.storage.getItem(this.key(key)) || "";
      if (this.toBeCompressed) {
        storageContent = LZString.decompressFromUTF16(storageContent) ?? "";
      }

      return JSON.parse(storageContent) as T;
    } catch (e) {
      return null;
    }
  };

  remove = (key: string): void => {
    if (this.storage) this.storage.removeItem(this.key(key));
  };
}
